/**
 * Utility Functions for Embedded Dashboard
 */
import type { VoiceSectionAccess } from '@/components/analytics/voice/VoiceLocked';
import type { FeatureGateState } from '@/contexts/FeatureFlagsContext';
import {
  decideScreen,
  isGatedScreen,
  managedAccessOf,
  screenForPath,
  waitsForAccess,
  type Screen,
  type ScreenSubject,
} from '@/lib/route-permissions';
import { EMBEDDED_TAB_BY_ROUTE, type Tab } from './types';

/**
 * The screen of the role map a tab shows — the screen of the portal page the
 * tab stands in for (`EMBEDDED_TAB_BY_ROUTE`), read from the one path→screen
 * table (`screenForPath`). No third hand-kept map: a tab whose page has no
 * screen is `undefined`, and `embeddedTabAllowed` refuses it (review #1678).
 */
export function embeddedTabScreen(tab: Tab): Screen | undefined {
  const route = Object.keys(EMBEDDED_TAB_BY_ROUTE).find(
    (path) => EMBEDDED_TAB_BY_ROUTE[path] === tab
  );
  return route === undefined ? undefined : screenForPath(route);
}

/**
 * May this subject open this tab — the decision the portal's shell gate makes
 * for the tab's page (#1645). The overview is never refused. A screen the gate
 * does not decide (`isGatedScreen`) opens whatever the role, as every tab but
 * Voice always did — a stale `null` role after an access request was approved
 * must not lose Records and Settings (review #1678). Only Voice is decided by
 * the role (#848); every other tab is narrowed by a resolved `managed` answer
 * alone.
 */
export function embeddedTabAllowed(tab: Tab, subject: ScreenSubject): boolean {
  if (tab === 'dashboard') return true;
  const screen = embeddedTabScreen(tab);
  if (!screen) return false;
  if (!isGatedScreen(screen, subject.access)) return true;
  return decideScreen(screen, subject) === 'allowed';
}

/**
 * What the dashboard shows for the tab the address asks for:
 *
 * - `show` — the tab, or the overview in place of a tab the subject may not
 *   open: the panel is hidden for that subject, and so is the tab that would
 *   lead out of it (#1379). `refusedByAccess` names the asked tab when its
 *   GRANT refused it — the address is then replaced and the refusal said
 *   (review #1678); a tab the role refuses, without a grant, is left as it
 *   always was (overview, address untouched);
 * - `waiting` — the role refuses the tab, but a grant could open it and
 *   `/access/me` has not answered: wait for the answer rather than mount the
 *   overview and jump (the portal gate waits the same way, #1645).
 */
export type EmbeddedTabResolution =
  | { status: 'show'; tab: Tab; refusedByAccess: Tab | null }
  | { status: 'waiting'; tab: Tab };

export function resolveEmbeddedTab(
  requested: Tab | undefined,
  subject: ScreenSubject
): EmbeddedTabResolution {
  const tab = requested ?? 'dashboard';
  if (embeddedTabAllowed(tab, subject)) {
    return { status: 'show', tab, refusedByAccess: null };
  }
  const screen = embeddedTabScreen(tab);
  if (screen && waitsForAccess(screen, subject.access)) {
    return { status: 'waiting', tab };
  }
  return {
    status: 'show',
    tab: 'dashboard',
    refusedByAccess: managedAccessOf(subject.access) ? tab : null,
  };
}

/**
 * The Voice of Customer access state from the session's two flags and its
 * verdict in the flag map (`gate`). "Could not ask" is not "not on your plan":
 * it offers to ask again instead of showing the plan lock for an outage
 * (code-review #1208). A plan below Pro (`tier_not_eligible`, #1897) is the
 * shared tier screen; any other "no" — a switch, or a session that carries no
 * verdict (an older edge-api) — keeps the shell's own words (`unavailable`).
 */
export function embeddedVoiceAccess(
  enabled: boolean,
  unchecked: boolean,
  gate: FeatureGateState = 'failed'
): VoiceSectionAccess {
  if (enabled) return 'enabled';
  if (unchecked) return 'failed';
  return gate === 'tier_not_eligible' ? gate : 'unavailable';
}

/**
 * Validates email address format
 * @param email - Email string to validate
 * @returns Valid email or empty string
 */
export function validateEmail(email: string): string {
  if (!email) return '';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) ? email : '';
}
